/**
 * /auth/reset-password — choose a password.
 *
 * The landing page for any Supabase email link that establishes a session and
 * expects the user to set a password:
 *   - password reset (the link sent by /auth/forgot), and
 *   - team-invite acceptance (the colleague invite — FO-2 / B2).
 *
 * Supabase has already set a session from the link (detectSessionInUrl), so we
 * only need to capture a new password and call `supabase.auth.updateUser`. On
 * success the user is signed in and continues into the app.
 *
 * B2 (invited-teammate bridge): when the session carries an invite breadcrumb
 * (`user_metadata.elreign_user_doc_id`, written by admin-team-invite-send), the
 * invitee's Supabase user has NO app_metadata.tenant_id yet, so every /v1/* call
 * would 403 NO_TENANT_ASSIGNMENT and lock them out. After the password is set we
 * therefore call POST /api/v1/auth/accept-invite (which links the Supabase id to
 * the pre-created UserDoc, stamps app_metadata, and reconciles trust memberIds),
 * then refreshSession() to pick up the freshly-stamped tenant claim, then route
 * by role. The plain password-reset path (no breadcrumb) is unchanged.
 *
 * W10 (2026-07-11, Paul's ruling — "invited staff MUST get MFA like the founder
 * does"): a freshly-accepted STAFF invitee (any FIRM_STAFF role — admin /
 * compliance / rm / director / super_admin) has never enrolled a second factor,
 * unlike the founder, who is hard-gated through MFA during firm onboarding
 * before ever reaching the console. Routing them straight to landingRouteForRole
 * (the firm console) would leave them password-only forever — the admin/_layout
 * gate (routes/admin/_layout.tsx) WOULD bounce them back out on their very next
 * load, but sending them there directly is both a wasted hop and a worse first
 * impression. Instead, route a staff invitee to the existing MFA enrolment
 * surface first; a portal client (role `user`) has no such gate.
 *
 * THE ARRIVAL TUTORIAL (2026-08-19). Whichever of those two paths they take, an
 * invited person now lands on `/onboarding/invited` rather than straight in the
 * console or the portal. Beno, 08-18: for someone who was INVITED the welcome is
 * the email — which now names the inviter, the firm and the position — and the
 * screen after it should be "a tutorial of where everything is and what the
 * functionality is… the position they've been assigned, what it means in this
 * app" (Paul, same call). Before this, an invited administrator's first sight of
 * Elreign was a console with eleven sidebar entries and no explanation of which
 * of them were theirs.
 *
 * The tutorial reads the role from `/v1/users/me`, never from this navigation,
 * so a hand-typed URL cannot conjure a different tour. It ends by sending them to
 * `landingRouteForRole` — the same destination this file used to navigate to
 * directly — so the tutorial is a stop on the way, never a detour.
 */

import { useEffect, useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { AuthLayout } from '../../components/layouts/auth-layout';
import { PasswordField, AuthSubmit } from '../../components/auth/auth-controls';
import { supabase } from '../../lib/supabase';
import { mapSupabaseAuthError, isInsufficientAalError } from '../../lib/auth-errors';
import { PASSWORD_MIN_LENGTH, passwordProblem } from '../../lib/password-policy';
import { useAcceptInvite } from '../../lib/api';
import { isFirmStaffRole } from '../../lib/authz/route-policy';

export const Route = createFileRoute('/auth/reset-password')({
  component: ResetPasswordPage,
});

/** GoTrue redirects a dead link with hash error params instead of tokens —
 *  `#error=access_denied&error_code=otp_expired&…`. Detect that up front so
 *  we never show a password form that cannot succeed. */
function hashCarriesLinkError(): boolean {
  try {
    const hash = window.location.hash.replace(/^#/, '');
    if (!hash) return false;
    const params = new URLSearchParams(hash);
    return params.has('error') || params.has('error_code');
  } catch {
    return false;
  }
}

/** How long we wait for detectSessionInUrl to materialise a session before
 *  declaring the link dead. Exported for tests. */
export const LINK_SESSION_TIMEOUT_MS = 4_000;

type LinkState = 'checking' | 'ready' | 'dead';

/**
 * AAL-1 (2026-07-30) — the MFA step this screen owns.
 *
 * A recovery-link session is `aal1`. GoTrue refuses `PUT /user` with
 * `insufficient_aal` ("AAL2 session is required to update email or password
 * when MFA is enabled") whenever the account has a verified factor — so a
 * password reset could NEVER complete for an MFA-enrolled user, and firm-staff
 * MFA is mandatory (W10). Observed live on prod: two 401s for a real user who
 * was then permanently stuck behind the generic "Sign-in failed" copy.
 *
 * So the reset screen challenges TOTP itself: `mfa.challenge` + `mfa.verify`
 * lift the recovery session to aal2, and only then does the password change go
 * out. Deliberately NOT `useStepUpChallenge` — that hook also POSTs
 * /v1/auth/refresh-step-up to stamp Elreign-side step-up freshness, which needs
 * a tenant-bearing JWT this pre-password session may not have. Changing a
 * password only needs Supabase-level aal2.
 */
interface MfaGate {
  /** Does this session have to pass a TOTP challenge before updateUser? */
  needed: boolean;
  /** The verified TOTP factor to challenge (null → we could not resolve one). */
  factorId: string | null;
}

/** First VERIFIED TOTP factor on the account, or null. Mirrors the discovery in
 *  use-step-up-challenge.ts (`data.all` filtered on `factor_type`), preferring
 *  the SDK's pre-filtered `data.totp` when present. Never throws. */
async function firstVerifiedTotpFactorId(): Promise<string | null> {
  try {
    const { data, error } = await supabase.auth.mfa.listFactors();
    if (error || !data) return null;
    const candidates = [...(data.totp ?? []), ...(data.all ?? [])] as Array<{
      id: string;
      factor_type?: string;
      status?: string;
    }>;
    const totp = candidates.find(
      (f) => (f.factor_type ?? 'totp') === 'totp' && (f.status ?? 'verified') === 'verified',
    );
    return totp?.id ?? null;
  } catch {
    return null;
  }
}

/** Lift the current session to aal2 with a TOTP code. Returns a user-safe
 *  message on failure (never a raw library string). */
async function elevateWithTotp(
  factorId: string | null,
  code: string,
): Promise<{ ok: true } | { ok: false; message: string }> {
  if (!factorId) {
    return {
      ok: false,
      message:
        'We could not find the authenticator on your account. Ask your administrator to reset ' +
        'two-factor authentication for you, then request a fresh password link.',
    };
  }
  try {
    const { data: challenge, error: chErr } = await supabase.auth.mfa.challenge({ factorId });
    if (chErr || !challenge?.id) {
      return { ok: false, message: mapSupabaseAuthError(chErr?.message) };
    }
    const { error: vErr } = await supabase.auth.mfa.verify({
      factorId,
      challengeId: challenge.id,
      code,
    });
    if (vErr) return { ok: false, message: mapSupabaseAuthError(vErr.message) };
    return { ok: true };
  } catch (err) {
    return { ok: false, message: mapSupabaseAuthError((err as Error)?.message) };
  }
}

function ResetPasswordPage() {
  const navigate = useNavigate();
  const acceptInvite = useAcceptInvite();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 2026-07-19 miss-hunt SCO-1: an expired/used link used to render the full
  // form anyway — the submit then failed with misleading "Sign-in failed"
  // copy. Resolve the link state FIRST: hash error → dead immediately;
  // otherwise wait briefly for the URL session, then declare dead honestly.
  const [linkState, setLinkState] = useState<LinkState>(() =>
    hashCarriesLinkError() ? 'dead' : 'checking',
  );
  // AAL-1: whether this session owes a TOTP challenge before the password change.
  const [mfa, setMfa] = useState<MfaGate>({ needed: false, factorId: null });
  const [code, setCode] = useState('');

  useEffect(() => {
    if (linkState !== 'checking') return;
    let cancelled = false;

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!cancelled && session) setLinkState('ready');
    });
    void supabase.auth.getSession().then(({ data }) => {
      if (!cancelled && data.session) setLinkState('ready');
    });
    const timer = window.setTimeout(() => {
      if (!cancelled) {
        setLinkState((s) => (s === 'checking' ? 'dead' : s));
      }
    }, LINK_SESSION_TIMEOUT_MS);

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
      window.clearTimeout(timer);
    };
  }, [linkState]);

  // AAL-1: once the link has produced a session, ask Supabase whether it owes an
  // MFA promotion (a verified factor exists that THIS session has not stepped up
  // to — the same `currentLevel/nextLevel` signal the /app and /admin gates use).
  // If the probe itself fails we do NOT block: the submit path below catches the
  // server's `insufficient_aal` rejection and opens the code field then, so a
  // flaky probe can never strand a user who has no factor at all.
  useEffect(() => {
    if (linkState !== 'ready') return;
    let cancelled = false;
    void (async () => {
      try {
        const { data, error: aalErr } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
        if (cancelled || aalErr || !data) return;
        if (data.currentLevel === 'aal1' && data.nextLevel === 'aal2') {
          const factorId = await firstVerifiedTotpFactorId();
          if (!cancelled) setMfa({ needed: true, factorId });
        }
      } catch {
        // Reactive fallback on submit — see above.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [linkState]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    setError(null);
    // SGN-02: the same four rules the checklist under the field draws, from the
    // same list. The message names what is still missing, never "invalid".
    const pwProblem = passwordProblem(password);
    if (pwProblem !== null) {
      setError(pwProblem);
      return;
    }
    if (password !== confirm) {
      setError("Those passwords don't match. Please re-enter them.");
      return;
    }

    // AAL-1: promote the recovery session to aal2 BEFORE the password change,
    // otherwise GoTrue rejects it outright for any MFA-enrolled account.
    if (mfa.needed) {
      const trimmed = code.trim();
      if (!/^\d{6}$/.test(trimmed)) {
        setError('Enter the 6-digit code from your authenticator app.');
        return;
      }
      setBusy(true);
      const elevated = await elevateWithTotp(mfa.factorId, trimmed);
      if (!elevated.ok) {
        setBusy(false);
        setCode('');
        setError(elevated.message);
        return;
      }
    }

    setBusy(true);
    const { error: updErr } = await supabase.auth.updateUser({ password });
    if (updErr) {
      setBusy(false);
      // The server says this session never passed an MFA challenge — surface the
      // code field (with honest copy) so the user finishes here instead of
      // bouncing off a dead end. Covers the case where the proactive probe above
      // could not run.
      if (isInsufficientAalError(updErr.message)) {
        const factorId = mfa.factorId ?? (await firstVerifiedTotpFactorId());
        setMfa({ needed: true, factorId });
      }
      setError(mapSupabaseAuthError(updErr.message));
      return;
    }

    // B2 — invited-teammate bridge. Inspect the session: if it carries the
    // invite breadcrumb the admin-team-invite flow wrote into user_metadata,
    // this is a colleague accepting an invite (not a plain password reset).
    // Bridge them before routing — otherwise their JWT has no tenant_id and
    // every /v1/* call 403s.
    let inviteDocId: unknown;
    try {
      const { data } = await supabase.auth.getSession();
      const meta = data.session?.user.user_metadata as
        | { elreign_user_doc_id?: unknown }
        | undefined;
      inviteDocId = meta?.elreign_user_doc_id;
    } catch {
      // No session readable — fall through to the plain path.
    }

    if (typeof inviteDocId === 'string' && inviteDocId.length > 0) {
      try {
        const res = await acceptInvite.mutateAsync();
        // Refresh the Supabase session so the new JWT carries the freshly
        // stamped app_metadata.tenant_id. Without this every downstream
        // /v1/* call would use the pre-bridge JWT and 403 NO_TENANT_ASSIGNMENT
        // until the user signs out and back in. Best-effort: if it fails the
        // user can still re-auth, so we don't block the route on it (unless
        // the backend told us it couldn't stamp metadata at all — below).
        try {
          await supabase.auth.refreshSession();
        } catch {
          // Non-fatal — the layout gates will prompt a re-auth if needed.
        }
        setBusy(false);
        if (res.noticeFlags?.supabaseAdminSkipped) {
          // Dev / degraded: the link landed in Cosmos but app_metadata wasn't
          // stamped, so the refreshed JWT still lacks tenant_id. Tell the user
          // plainly rather than bouncing them into a 403 loop.
          setError(
            "Your invitation was accepted, but we couldn't finish setting up your access automatically. " +
              'Please sign out and sign back in. If the problem persists, contact your administrator.',
          );
          return;
        }
        // W10: a staff invitee has never enrolled a second factor — send them
        // to enrol now instead of landing them password-only in the console. A
        // portal client (`user`) is not firm staff and skips that gate.
        //
        // ORDER (2026-08-19): security gate FIRST, orientation second. The
        // enrolment's return destination is the arrival tutorial, which then
        // hands off to `landingRouteForRole` itself. Putting the tutorial before
        // MFA would delay the one thing on this path that is not optional.
        if (isFirmStaffRole(res.profile.role)) {
          void navigate({ to: '/auth/mfa-enroll', search: { next: '/onboarding/invited' } as never });
          return;
        }
        void navigate({ to: '/onboarding/invited' });
        return;
      } catch {
        // accept-invite failed — don't strand the user on a blank page.
        setBusy(false);
        setError(
          "We couldn't finish accepting your invitation. Your password has been set. " +
            'Please sign in. If you still cannot get in, ask your administrator to re-send the invite.',
        );
        return;
      }
    }

    // Plain password-reset path (no invite breadcrumb) — unchanged.
    setBusy(false);
    void navigate({ to: '/app' });
  }

  const describedBy = error !== null ? 'reset-error' : undefined;

  if (linkState === 'dead') {
    return (
      <AuthLayout
        eyebrow="Account access"
        heading="This link has expired."
        sub="Password links are single-use and only valid for a short time."
      >
        <div
          data-testid="reset-link-dead"
          style={{ fontSize: 14, color: 'var(--ink-2)', lineHeight: 1.6 }}
        >
          <p style={{ margin: 0 }}>
            Request a fresh link and try again. It only takes a moment.
          </p>
          <p style={{ margin: '12px 0 0' }}>
            <Link
              to="/auth/forgot"
              style={{ color: 'var(--primary-600)', fontWeight: 600 }}
            >
              Request a new link
            </Link>
          </p>
          <p style={{ margin: '16px 0 0', fontSize: 13, color: 'var(--ink-3)' }}>
            Invited by your firm? Ask your administrator to re-send the
            invitation instead.
          </p>
          <p style={{ margin: '12px 0 0', fontSize: 13, color: 'var(--ink-3)' }}>
            Already have a password?{' '}
            <Link to="/auth/login" style={{ color: 'var(--primary-600)', fontWeight: 500 }}>
              Back to sign in
            </Link>
            .
          </p>
        </div>
      </AuthLayout>
    );
  }

  if (linkState === 'checking') {
    return (
      <AuthLayout
        eyebrow="Account access"
        heading="Checking your link…"
        sub="One moment while we confirm this password link."
      >
        <div
          data-testid="reset-link-checking"
          aria-live="polite"
          style={{ fontSize: 14, color: 'var(--ink-3)' }}
        >
          Confirming…
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      eyebrow="Account access"
      heading="Choose your password."
      sub="Set a password for your Elreign account to finish signing in."
    >
      <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <PasswordField
          id="reset-password"
          label="New password"
          placeholder="New password"
          requirementsId="reset-password-requirements"
          value={password}
          onChange={setPassword}
          autoComplete="new-password"
          ariaInvalid={error !== null}
          ariaDescribedby={describedBy}
          minLength={PASSWORD_MIN_LENGTH}
          disabled={busy}
        />

        <PasswordField
          id="reset-confirm"
          label="Confirm password"
          placeholder="Re-enter new password"
          value={confirm}
          onChange={setConfirm}
          autoComplete="new-password"
          ariaInvalid={error !== null}
          ariaDescribedby={describedBy}
          disabled={busy}
        />

        {/* AAL-1: only rendered for an MFA-enrolled account. GoTrue will not let
            the password change through without it, so asking here is the only way
            this screen can succeed for firm staff. */}
        {mfa.needed && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label
              htmlFor="reset-totp"
              style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-2)' }}
            >
              Authenticator code
            </label>
            <input
              id="reset-totp"
              name="one-time-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              placeholder="123456"
              value={code}
              onChange={(ev) => setCode(ev.target.value.replace(/\D/g, '').slice(0, 6))}
              disabled={busy}
              aria-invalid={error !== null}
              aria-describedby={describedBy}
              style={{
                padding: '10px 12px',
                borderRadius: 8,
                border: '1px solid var(--hairline)',
                fontSize: 16,
                letterSpacing: '0.28em',
                fontVariantNumeric: 'tabular-nums',
              }}
            />
            <p style={{ margin: 0, fontSize: 12.5, color: 'var(--ink-3)', lineHeight: 1.5 }}>
              Your account uses two-factor authentication, so we need the current
              6-digit code from your authenticator app to confirm this change.
            </p>
          </div>
        )}

        {error !== null && (
          <div
            id="reset-error"
            role="alert"
            aria-live="polite"
            style={{
              padding: '10px 12px',
              borderRadius: 8,
              background: 'rgba(220, 38, 38, 0.08)',
              border: '1px solid rgba(220, 38, 38, 0.3)',
              color: '#991B1B',
              fontSize: 13,
              lineHeight: 1.45,
            }}
          >
            {error}
          </div>
        )}

        <AuthSubmit label="Set password" busyLabel="Setting…" busy={busy} />
      </form>

      <div
        style={{
          marginTop: 24,
          paddingTop: 18,
          borderTop: '1px solid var(--hairline)',
          fontSize: 13.5,
          color: 'var(--ink-3)',
          lineHeight: 1.6,
        }}
      >
        Already have a password?{' '}
        <Link to="/auth/login" style={{ color: 'var(--primary-600)', fontWeight: 500 }}>
          Back to sign in
        </Link>
        .
      </div>
    </AuthLayout>
  );
}
